import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"BUILDID":"c3cb6cb075c3005a9bfc","DOMAIN_UPDATE_URL":"https://oxbetvn.top","DOMAIN_UPDATE_FROMDATE":"28/05/2021","ENABLE_DOMAIN_UPDATE":"0","API_URL":"http://localhost:3003","MAIN_DOMAIN":"localhost:3000","REFRESH_INTERVAL":"5000","JACKPOT_INTERVAL":"3220","MAINTAIN":"0","MAINTAIN_EURO":"0","MAINTAIN_ASIA":"0","MAINTAIN_KSPORT":"0","SWITCH_VERSION":"1","PWA":"0","CHAT_BOX":"0","NOINDEX":"0","LLQP":"1","MINIGAME":"1","SLOT_URL":"https://in.engstd.xyz","CHECK_IP":"1","COUNTRY_CODE":"KH,LA","IP_SUBNETS":"175.100.42.0/24","LIVECHAT_MAINTAIN":"0","EVENT_WHEEL":"1","EVENT_NOEL":"0","EVENT_HOLIDAY":"0","EVENT_EURO":"0","EVENT_OLYMPIC":"0","EVENT_BIRTHDAY":"0","EVENT_SEAGAME":"1","EVENT_SEAGAME_API_PATH":"https://oxbet.net/stg-event","EVENT_SEAGAME_API_MEDAL":"https://accreditationapi.seagames2021.vn/api/result/medaltable","END_DATE_SEAGAMES_EVENT":"2022/06/01 00:00:00","ENABLE_SHOW_MERGE_WALLET":"1","IS_MAINTAIN_PHONE_CARD_DEPOSIT":"0","MAINTAIN_SITE":"0","EVENT_WORLD_CUP_API_PATH":"https://dev06.oxbet.com/event","EVENT_WORLDCUP":"1","JACKPOT_URL_LINK":"https://api-jp.gwjackpost.net/glms/v1/jp","ENABLE_LABEL_EVENT":"1","EVENT_GOAL_SCORER":"0","EVENT_WORLD_CUP_GOAL_API_WC_MATCH":"https://dev05.oxbet.com/api/v1/hotmatch","CREATED_TIME":"2022-12-20","NVM_INC":"/home/msservice/.nvm/versions/node/v14.21.3/include/node","npm_package_devDependencies_resolve_url_loader":"^3.1.1","XDG_SESSION_ID":"13021","HOSTNAME":"oxb-ob002-stg-fe","npm_package_dependencies_animated_number_vue":"^1.0.0","NODE":"/home/msservice/.nvm/versions/node/v14.21.3/bin/node","INIT_CWD":"/home/msservice/sources/stage/fe","NVM_CD_FLAGS":"","npm_config_version_git_tag":"true","TERM":"xterm-256color","SHELL":"/bin/bash","npm_package_dependencies_vue_scrollto":"^2.18.2","npm_package_dependencies_normalize_css":"^8.0.1","HISTSIZE":"1000","npm_package_dependencies_text_mask_addons":"^3.8.0","npm_package_devDependencies_nuxt_font_loader":"^1.1.3","npm_package_dependencies__nuxtjs_axios":"^5.12.2","npm_config_init_license":"MIT","npm_package_dependencies_vue_text_mask":"^6.1.2","npm_package_scripts_nuxt_watch_prod":"nuxt -c ./config_pro.js && nuxt start -c ./config_pro.js","npm_package_scripts_dev":"nodemon --watch api","npm_package_dependencies_vue_range_slider":"^0.6.0","npm_package_dependencies__nuxtjs_device":"^1.2.7","npm_package_devDependencies_sass_loader":"^8.0.2","npm_package_resolutions_vue_router":"3.1.2","npm_package_devDependencies_webpack_bundle_analyzer":"^3.9.0","npm_package_dependencies_pixi_spine":"^3.0.16","npm_package_dependencies__babel_preset_env":"7.12.13","npm_config_registry":"https://registry.yarnpkg.com","npm_package_devDependencies_bootstrap_vue":"^2.17.3","npm_package_scripts_restart":"nuxt build -c ./config_pro.js && pm2 restart kimbai","npm_package_dependencies_body_scroll_lock":"^3.1.5","npm_package_readmeFilename":"README.md","npm_package_devDependencies_eslint_plugin_jsx_a11y":"^6.3.1","npm_package_dependencies_vue2_datepicker":"^3.6.2","npm_package_dependencies_dayjs":"^1.8.36","USER":"msservice","NVM_DIR":"/home/msservice/.nvm","npm_package_description":"# Dev yarn install yarn nuxt-watch # SERVER BUILD ## Required node: v12.12.0 pm2 ## Build + Build Vue ```bash yarn install yarn test ( restest ) ```","LS_COLORS":"rs=0:di=38;5;27:ln=38;5;51:mh=44;38;5;15:pi=40;38;5;11:so=38;5;13:do=38;5;5:bd=48;5;232;38;5;11:cd=48;5;232;38;5;3:or=48;5;232;38;5;9:mi=05;48;5;232;38;5;15:su=48;5;196;38;5;15:sg=48;5;11;38;5;16:ca=48;5;196;38;5;226:tw=48;5;10;38;5;16:ow=48;5;10;38;5;21:st=48;5;21;38;5;15:ex=38;5;34:*.tar=38;5;9:*.tgz=38;5;9:*.arc=38;5;9:*.arj=38;5;9:*.taz=38;5;9:*.lha=38;5;9:*.lz4=38;5;9:*.lzh=38;5;9:*.lzma=38;5;9:*.tlz=38;5;9:*.txz=38;5;9:*.tzo=38;5;9:*.t7z=38;5;9:*.zip=38;5;9:*.z=38;5;9:*.Z=38;5;9:*.dz=38;5;9:*.gz=38;5;9:*.lrz=38;5;9:*.lz=38;5;9:*.lzo=38;5;9:*.xz=38;5;9:*.bz2=38;5;9:*.bz=38;5;9:*.tbz=38;5;9:*.tbz2=38;5;9:*.tz=38;5;9:*.deb=38;5;9:*.rpm=38;5;9:*.jar=38;5;9:*.war=38;5;9:*.ear=38;5;9:*.sar=38;5;9:*.rar=38;5;9:*.alz=38;5;9:*.ace=38;5;9:*.zoo=38;5;9:*.cpio=38;5;9:*.7z=38;5;9:*.rz=38;5;9:*.cab=38;5;9:*.jpg=38;5;13:*.jpeg=38;5;13:*.gif=38;5;13:*.bmp=38;5;13:*.pbm=38;5;13:*.pgm=38;5;13:*.ppm=38;5;13:*.tga=38;5;13:*.xbm=38;5;13:*.xpm=38;5;13:*.tif=38;5;13:*.tiff=38;5;13:*.png=38;5;13:*.svg=38;5;13:*.svgz=38;5;13:*.mng=38;5;13:*.pcx=38;5;13:*.mov=38;5;13:*.mpg=38;5;13:*.mpeg=38;5;13:*.m2v=38;5;13:*.mkv=38;5;13:*.webm=38;5;13:*.ogm=38;5;13:*.mp4=38;5;13:*.m4v=38;5;13:*.mp4v=38;5;13:*.vob=38;5;13:*.qt=38;5;13:*.nuv=38;5;13:*.wmv=38;5;13:*.asf=38;5;13:*.rm=38;5;13:*.rmvb=38;5;13:*.flc=38;5;13:*.avi=38;5;13:*.fli=38;5;13:*.flv=38;5;13:*.gl=38;5;13:*.dl=38;5;13:*.xcf=38;5;13:*.xwd=38;5;13:*.yuv=38;5;13:*.cgm=38;5;13:*.emf=38;5;13:*.axv=38;5;13:*.anx=38;5;13:*.ogv=38;5;13:*.ogx=38;5;13:*.aac=38;5;45:*.au=38;5;45:*.flac=38;5;45:*.mid=38;5;45:*.midi=38;5;45:*.mka=38;5;45:*.mp3=38;5;45:*.mpc=38;5;45:*.ogg=38;5;45:*.ra=38;5;45:*.wav=38;5;45:*.axa=38;5;45:*.oga=38;5;45:*.spx=38;5;45:*.xspf=38;5;45:","npm_package_devDependencies__nuxt_image":"^0.4.1","npm_package_dependencies_vue_clipboard2":"^0.3.1","npm_package_devDependencies_eslint_loader":"^4.0.2","npm_package_dependencies_vue_touch":"^2.0.0-beta.4","npm_package_dependencies__nuxtjs_pwa":"^3.0.2","npm_package_devDependencies_eslint_friendly_formatter":"^4.0.1","npm_package_scripts_build_js":"","npm_package_dependencies_slick_carousel":"^1.8.1","npm_package_devDependencies_eslint":"^6.8.0","npm_package_dependencies_vue_qr":"^2.5.0","npm_execpath":"/home/msservice/.nvm/versions/node/v14.21.3/lib/node_modules/yarn/bin/yarn.js","npm_package_scripts_build_js_prod":"","npm_package_dependencies_sass_mq":"^5.0.1","npm_package_dependencies_vuejs_datepicker":"^1.6.2","PATH":"/tmp/yarn--1687161171856-0.08768314213306416:/home/msservice/sources/stage/fe/node_modules/.bin:/home/msservice/.config/yarn/link/node_modules/.bin:/home/msservice/.yarn/bin:/home/msservice/.nvm/versions/node/v14.21.3/libexec/lib/node_modules/npm/bin/node-gyp-bin:/home/msservice/.nvm/versions/node/v14.21.3/lib/node_modules/npm/bin/node-gyp-bin:/home/msservice/.nvm/versions/node/v14.21.3/bin/node_modules/npm/bin/node-gyp-bin:/home/msservice/.nvm/versions/node/v14.21.3/bin:/usr/local/bin:/bin:/usr/bin:/usr/local/sbin:/usr/sbin:/home/msservice/.local/bin:/home/msservice/bin","MAIL":"/var/spool/mail/msservice","npm_config_argv":"{\"remain\":[],\"cooked\":[\"run\",\"nuxt\"],\"original\":[\"nuxt\",\"build\",\"-c\",\"./config_dev.js\"]}","npm_package_dependencies_swiper":"^5.4.5","npm_package_dependencies_vuelidate":"^0.7.5","npm_package_dependencies_netmask":"^2.0.2","_":"/home/msservice/sources/stage/fe/node_modules/.bin/nuxt","PWD":"/home/msservice/sources/stage/fe","npm_package_devDependencies_eslint_config_airbnb":"^18.2.0","npm_package_dependencies_create_hmac":"^1.1.7","npm_package_dependencies_bootstrap":"^4.5.2","npm_package_dependencies__nuxtjs_sitemap":"^2.4.0","npm_lifecycle_event":"nuxt","LANG":"en_US.UTF-8","npm_package_name":"kimbai","npm_package_devDependencies_eslint_plugin_import":"^2.22.0","npm_package_devDependencies_sass":"^1.26.11","npm_package_scripts_start":"nuxt build -c ./config_pro.js && pm2 start","npm_package_scripts_build":"nuxt build","npm_config_version_commit_hooks":"true","npm_package_scripts_post_update":"yarn upgrade --latest","npm_package_dependencies_vue_lazyload":"^1.3.3","npm_config_bin_links":"true","npm_package_dependencies_vue_i18n":"^8.21.1","HISTCONTROL":"ignoredups","npm_package_version":"","npm_package_devDependencies_eslint_config_airbnb_base":"^14.2.0","npm_package_scripts_watch":"","npm_package_dependencies_sweetalert2":"^9.17.2","npm_package_dependencies_cookie_universal_nuxt":"^2.1.4","HOME":"/home/msservice","SHLVL":"3","npm_package_dependencies_pixi_js":"5.3.12","npm_package_dependencies__nuxtjs_gtm":"^2.3.2","npm_package_devDependencies_eslint_plugin_vue":"^6.2.2","npm_package_scripts_test":"nuxt build -c ./config_dev.js && pm2 start","npm_package_dependencies_animate_css":"^3.7.2","npm_package_dependencies_dotenv":"^8.2.0","npm_config_save_prefix":"^","npm_config_strict_ssl":"true","npm_package_devDependencies_babel_eslint":"^10.1.0","npm_config_version_git_message":"v%s","npm_package_scripts_retest":"nuxt build -c ./config_dev.js && pm2 restart kimbai","npm_package_scripts_nuxt_watch":"nuxt -c ./config_dev.js && nuxt start -c ./config_dev.js","npm_package_devDependencies__nuxtjs_dotenv":"^1.4.1","LOGNAME":"msservice","YARN_WRAP_OUTPUT":"false","npm_package_dependencies_vuex_persistedstate":"^2.5.4","LESSOPEN":"||/usr/bin/lesspipe.sh %s","NVM_BIN":"/home/msservice/.nvm/versions/node/v14.21.3/bin","npm_package_dependencies_jquery":"^3.5.1","npm_package_dependencies_v_money":"^0.8.1","npm_package_dependencies_nuxt_ssr_cache":"^1.5.2","npm_config_version_git_sign":"","npm_config_ignore_scripts":"","npm_config_user_agent":"yarn/1.22.19 npm/? node/v14.21.3 linux x64","npm_package_dependencies_nuxt":"^2.12.2","npm_package_dependencies_animejs":"^3.2.0","npm_package_dependencies__nuxtjs_style_resources":"^1.0.0","npm_config_init_version":"1.0.0","npm_config_ignore_optional":"","npm_package_devDependencies_node_sass":"^4.14.1","npm_node_execpath":"/home/msservice/.nvm/versions/node/v14.21.3/bin/node","npm_package_devDependencies_eslint_plugin_react":"^7.20.6","npm_package_dependencies_nuxt_webfontloader":"^1.1.0","npm_config_version_tag_prefix":"v","NODE_ENV":"production","_applied":"true","VUE_ENV":"server"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
